import ExcelJS from 'exceljs';
import type { SessionDetailPlayerResult, Expense } from '@/ui/view-props';

const THEME = {
  SLATE_900: 'FF0F172A',
  SLATE_800: 'FF1E293B',
  SLATE_700: 'FF334155',
  DARK_GREEN: 'FF064E3B',
  GREEN_400: 'FF4ADE80',
  RED_400: 'FFFB7185',
  AMBER_400: 'FFFBBF24',
  WHITE: 'FFFFFFFF',
};

const ACCOUNTING_FORMAT = '+$#,##0.00;-$#,##0.00;"—"';
const FONT_FAMILY = 'Segoe UI';

export async function exportSessionXlsx(
  sessionDate: string,
  results: SessionDetailPlayerResult[],
  expenses: Expense[],
  buyInUnit: number,
  transactions?: { from: string; to: string; amount: number }[],
  notes?: string | null,
  venue?: string | null,
  chartImage?: string
): Promise<void> {
  const dateStr = sessionDate.split('T')[0];
  const wb = new ExcelJS.Workbook();
  wb.creator = 'SSC Score Tracker';
  
  const summaryWs = wb.addWorksheet('Summary', { views: [{ showGridLines: false }] });
  const detailedWs = wb.addWorksheet('Detailed', { views: [{ showGridLines: false }] });

  const applyBaseStyle = (cell: ExcelJS.Cell) => {
    cell.font = { name: FONT_FAMILY, size: 10, color: { argb: THEME.WHITE } };
    cell.alignment = { vertical: 'middle' };
  };

  const applyBorder = (cell: ExcelJS.Cell) => {
    cell.border = {
      top: { style: 'thin', color: { argb: THEME.SLATE_700 } },
      bottom: { style: 'thin', color: { argb: THEME.SLATE_700 } },
      left: { style: 'thin', color: { argb: THEME.SLATE_700 } },
      right: { style: 'thin', color: { argb: THEME.SLATE_700 } },
    };
  };

  const styleHeader = (row: ExcelJS.Row, bgColor: string = THEME.SLATE_900) => {
    row.height = 35;
    row.eachCell((cell) => {
      applyBaseStyle(cell);
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      cell.font = { ...cell.font, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      applyBorder(cell);
    });
  };

  const styleDataRow = (row: ExcelJS.Row, index: number) => {
    row.height = 22;
    const isEven = index % 2 === 0;
    row.eachCell((cell) => {
      applyBaseStyle(cell);
      cell.fill = { 
        type: 'pattern', 
        pattern: 'solid', 
        fgColor: { argb: isEven ? THEME.SLATE_800 : THEME.SLATE_900 } 
      };
      applyBorder(cell);
    });
  };

  // ─────────────── SUMMARY SHEET ───────────────

  summaryWs.getColumn(1).width = 25;
  summaryWs.getColumn(2).width = 20;
  summaryWs.getColumn(3).width = 20;
  summaryWs.getColumn(4).width = 20;

  // Title Row
  summaryWs.mergeCells('A1:D1');
  const titleCell = summaryWs.getCell('A1');
  titleCell.value = 'SSC SESSION SETTLEMENT SUMMARY';
  summaryWs.getRow(1).height = 35;
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: THEME.DARK_GREEN } };
  titleCell.font = { name: FONT_FAMILY, size: 14, bold: true, color: { argb: THEME.WHITE } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };

  // Metadata block
  const metaRows = [
    ['DATE', dateStr],
    ['VENUE', venue || 'N/A'],
    ['BUY-IN UNIT', buyInUnit],
  ];

  metaRows.forEach((m, i) => {
    const rowNum = 3 + i;
    const row = summaryWs.getRow(rowNum);
    row.getCell(1).value = m[0];
    row.getCell(2).value = m[1];
    
    row.getCell(1).font = { name: FONT_FAMILY, size: 9, bold: true, color: { argb: THEME.AMBER_400 } };
    row.getCell(2).font = { name: FONT_FAMILY, size: 10, color: { argb: THEME.WHITE } };
    row.getCell(2).alignment = { vertical: 'middle', horizontal: 'center' };
    
    if (m[0] === 'BUY-IN UNIT') {
      row.getCell(2).numFmt = '"₹"#,##0.00';
    }
  });

  // Player Summary Table
  summaryWs.addRow([]);
  const pSumHdrRow = summaryWs.lastRow.number + 1;
  summaryWs.getRow(pSumHdrRow).getCell(1).value = 'PLAYER PERFORMANCE';
  summaryWs.getRow(pSumHdrRow).getCell(1).font = { name: FONT_FAMILY, size: 11, bold: true, color: { argb: THEME.WHITE } };

  const pSumHdr = summaryWs.addRow(['PLAYER', 'GROSS', 'EXPENSES', 'TOTAL NET']);
  styleHeader(pSumHdr);
  pSumHdr.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };

  results.forEach((r, i) => {
    const gross = r.net;
    const appliedExp = r.expensePaid - r.expenseShare;
    const row = summaryWs.addRow([r.name, gross, appliedExp, r.totalNet]);
    styleDataRow(row, i);
    
    row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
    
    [2, 3, 4].forEach(col => {
      const cell = row.getCell(col);
      cell.numFmt = ACCOUNTING_FORMAT;
      cell.alignment = { vertical: 'middle', horizontal: 'right' };
      
      if (col === 2 || col === 4) {
        const val = Number(cell.value) || 0;
        cell.font = { 
          ...cell.font, 
          color: { argb: val >= 0 ? THEME.GREEN_400 : THEME.RED_400 },
          bold: true 
        };
      }
    });
  });

  // Settlement block
  if (transactions && transactions.length > 0) {
    summaryWs.addRow([]);
    const setStartRow = summaryWs.lastRow.number + 1;
    summaryWs.getRow(setStartRow).getCell(1).value = 'SETTLEMENT INSTRUCTIONS';
    summaryWs.getRow(setStartRow).getCell(1).font = { name: FONT_FAMILY, size: 11, bold: true, color: { argb: THEME.WHITE } };
    
    const setHdr = summaryWs.addRow(['FROM (DEBTOR)', 'TO (CREDITOR)', 'AMOUNT', 'STATUS']);
    styleHeader(setHdr);
    setHdr.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };

    transactions.forEach((t, i) => {
      const row = summaryWs.addRow([t.from, t.to, t.amount, 'PENDING']);
      styleDataRow(row, i);
      row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
      row.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' };
      row.getCell(3).numFmt = ACCOUNTING_FORMAT;
      row.getCell(3).alignment = { vertical: 'middle', horizontal: 'right' };
      row.getCell(3).font = { ...row.getCell(3).font, bold: true };
      row.getCell(4).alignment = { vertical: 'middle', horizontal: 'center' };
    });
  }

  // Reconciliation Audit
  summaryWs.addRow([]);
  const auditStartRow = summaryWs.lastRow.number + 1;
  const totalNetSum = results.reduce((s, r) => s + r.totalNet, 0);
  const isBalanced = Math.abs(totalNetSum) < 0.01;

  summaryWs.getRow(auditStartRow).getCell(1).value = 'RECONCILIATION AUDIT';
  summaryWs.getRow(auditStartRow).getCell(1).font = { name: FONT_FAMILY, size: 11, bold: true, color: { argb: THEME.WHITE } };

  const auditHdr = summaryWs.addRow(['METRIC', 'VALUE']);
  styleHeader(auditHdr, THEME.SLATE_700);
  auditHdr.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
  
  const totalGross = results.reduce((s, r) => s + r.net, 0);
  const totalExpPool = expenses.reduce((s, e) => s + Number(e.amount), 0);

  const auditData = [
    ['TOTAL GROSS VOLUME', totalGross],
    ['EXPENSE POOL TOTAL', totalExpPool],
    ['NET BALANCE', totalNetSum],
    ['RECONCILIATION STATUS', isBalanced ? 'BALANCED' : 'UNBALANCED']
  ];

  auditData.forEach((d, i) => {
    const row = summaryWs.addRow(d);
    styleDataRow(row, i);
    row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
    row.getCell(1).font = { ...row.getCell(1).font, bold: true };
    if (typeof d[1] === 'number') {
      row.getCell(2).numFmt = ACCOUNTING_FORMAT;
      row.getCell(2).alignment = { vertical: 'middle', horizontal: 'right' };
    } else {
      row.getCell(2).alignment = { vertical: 'middle', horizontal: 'center' };
      row.getCell(2).font = { 
        ...row.getCell(2).font, 
        bold: true,
        color: { argb: isBalanced ? THEME.GREEN_400 : THEME.RED_400 }
      };
    }
  });

  if (notes?.trim()) {
    summaryWs.addRow([]);
    const noteHeaderRow = summaryWs.lastRow.number + 1;
    summaryWs.getRow(noteHeaderRow).getCell(1).value = 'SESSION NOTES';
    summaryWs.getRow(noteHeaderRow).getCell(1).font = { name: FONT_FAMILY, size: 11, bold: true, color: { argb: THEME.WHITE } };
    
    const notesContent = summaryWs.addRow([notes.trim()]);
    notesContent.height = Math.max(40, Math.ceil(notes.trim().length / 80) * 15);
    const cell = notesContent.getCell(1);
    summaryWs.mergeCells(`A${notesContent.number}:D${notesContent.number}`);
    applyBaseStyle(cell);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: THEME.SLATE_800 } };
    cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
    cell.font = { ...cell.font, italic: true };
    applyBorder(cell);
  }

  // Chart Image Support
  if (chartImage && chartImage.startsWith('data:image')) {
    try {
      const imageId = wb.addImage({
        base64: chartImage,
        extension: 'png',
      });
      summaryWs.addImage(imageId, {
        tl: { col: 5, row: 1 },
        ext: { width: 480, height: 280 }
      });
    } catch (e) {
      console.error('Failed to add chart image to Excel', e);
    }
  }

  // ─────────────── DETAILED SHEET ───────────────

  detailedWs.getColumn(1).width = 25;
  detailedWs.getColumn(2).width = 15;
  detailedWs.getColumn(3).width = 15;
  detailedWs.getColumn(4).width = 18;
  detailedWs.getColumn(5).width = 18;
  detailedWs.getColumn(6).width = 18;

  detailedWs.addRow(['DETAILED BALANCE WORKSHEET']).font = { name: FONT_FAMILY, size: 14, bold: true, color: { argb: THEME.WHITE } };
  detailedWs.addRow([]);

  const balHdr = detailedWs.addRow(['PLAYER', 'BUY IN', 'CASH OUT', 'GROSS', 'EXPENSES', 'TOTAL NET']);
  styleHeader(balHdr);
  balHdr.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };

  const balStartRow = detailedWs.lastRow.number + 1;
  results.forEach((r, i) => {
    const buyInAmt = r.buyIns * buyInUnit;
    const row = detailedWs.addRow([r.name, buyInAmt, r.cashOut, r.net, r.expensePaid - r.expenseShare, r.totalNet]);
    styleDataRow(row, i);
    row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
    
    [2, 3, 4, 5, 6].forEach(col => {
      const cell = row.getCell(col);
      cell.numFmt = ACCOUNTING_FORMAT;
      cell.alignment = { vertical: 'middle', horizontal: 'right' };
      if (col === 4 || col === 6) {
        const val = Number(cell.value) || 0;
        cell.font = { ...cell.font, bold: true, color: { argb: val >= 0 ? THEME.GREEN_400 : THEME.RED_400 } };
      }
    });
  });
  const balEndRow = detailedWs.lastRow.number;

  // Validation Row
  const validationRow = detailedWs.addRow(['VALIDATION CHECK', '', '', '', '', {
    formula: `IF(ABS(SUM(F${balStartRow}:F${balEndRow}))<0.01, "OK", "ERROR")`,
  }]);
  styleHeader(validationRow, THEME.SLATE_700);
  validationRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
  validationRow.getCell(6).alignment = { vertical: 'middle', horizontal: 'center' };

  detailedWs.addRow([]);
  detailedWs.addRow([]);

  // Expense Matrix
  detailedWs.addRow(['EXPENSE DISTRIBUTION MATRIX']).font = { name: FONT_FAMILY, size: 14, bold: true, color: { argb: THEME.WHITE } };
  detailedWs.addRow([]);

  const playerNames = results.map(r => r.name);
  const matrixHdr = ['DESCRIPTION', 'PAYER', 'TOTAL', ...playerNames.map(n => n.toUpperCase())];
  const matrixHdrRow = detailedWs.addRow(matrixHdr);
  styleHeader(matrixHdrRow);
  matrixHdrRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
  matrixHdrRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' };

  // Adjust widths for player columns in matrix
  playerNames.forEach((_, idx) => {
    detailedWs.getColumn(4 + idx).width = 15;
  });

  if (expenses.length > 0) {
    const matrixStartRow = detailedWs.lastRow.number + 1;
    expenses.forEach((e, i) => {
      const rowData = [
        e.description || 'Expense',
        e.payer?.name || 'Unknown',
        Number(e.amount)
      ];
      playerNames.forEach(pName => {
        const participant = e.participants?.find(p => p.player?.name === pName);
        rowData.push(participant ? Number(participant.share) : 0);
      });
      const row = detailedWs.addRow(rowData);
      styleDataRow(row, i);
      row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
      row.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' };
      
      for (let col = 3; col <= row.cellCount; col++) {
        const cell = row.getCell(col);
        cell.numFmt = ACCOUNTING_FORMAT;
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
        if (col > 3 && (Number(cell.value) || 0) !== 0) {
          cell.font = { ...cell.font, bold: true };
        }
      }
    });
    const matrixEndRow = detailedWs.lastRow.number;

    const totalsRow = detailedWs.addRow([
      'TOTALS', 
      '', 
      { formula: `SUM(C${matrixStartRow}:C${matrixEndRow})` } as any,
      ...playerNames.map((_, idx) => {
        const colLetter = detailedWs.getColumn(4 + idx).letter;
        return { formula: `SUM(${colLetter}${matrixStartRow}:${colLetter}${matrixEndRow})` };
      })
    ]);
    styleHeader(totalsRow, THEME.SLATE_700);
    totalsRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
    for (let col = 3; col <= totalsRow.cellCount; col++) {
      totalsRow.getCell(col).numFmt = ACCOUNTING_FORMAT;
      totalsRow.getCell(col).alignment = { vertical: 'middle', horizontal: 'right' };
    }
  } else {
    const noExpRow = detailedWs.addRow(['No expenses recorded for this session']);
    detailedWs.mergeCells(`A${noExpRow.number}:${detailedWs.getColumn(matrixHdr.length).letter}${noExpRow.number}`);
    noExpRow.height = 22;
    noExpRow.getCell(1).font = { name: FONT_FAMILY, italic: true, color: { argb: THEME.WHITE } };
    noExpRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: THEME.SLATE_800 } };
    applyBorder(noExpRow.getCell(1));
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `SSC_Settlement_${dateStr}.xlsx`;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
